import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { formatCurrency } from '../../utils/Currency';

const CashFlowChart = ({ data, currency, showValues, loading }) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data
      .slice(0, 12)
      .map((item) => ({
        period: item.period,
        cashIn: item.cashIn,
        cashOut: item.cashOut,
        netBalance: item.netBalance,
        savingsRate: item.savingsRate,
      }))
      .reverse();
  }, [data]);

  const totals = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const totalCashIn = chartData.reduce((sum, item) => sum + item.cashIn, 0);
    const totalCashOut = chartData.reduce((sum, item) => sum + item.cashOut, 0);
    const totalNet = totalCashIn - totalCashOut;
    const avgSavingsRate = totalCashIn > 0 ? (totalNet / totalCashIn) * 100 : 0;

    return {
      cashIn: totalCashIn,
      cashOut: totalCashOut,
      netBalance: totalNet,
      savingsRate: avgSavingsRate,
    };
  }, [chartData]);

  const yAxisConfig = useMemo(() => {
    if (chartData.length === 0) return { min: 0, max: 100 };

    const values = chartData.flatMap((d) => [d.cashIn || 0, d.cashOut || 0]);
    const maxVal = Math.max(...values);

    return {
      min: 0,
      max: maxVal === 0 ? 100 : maxVal * 1.1,
    };
  }, [chartData]);

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: theme.palette.text.secondary,
            display: 'block',
          }}
        >
          Fluxo de Caixa
        </Typography>
        {totals && (
          <Box sx={{ mt: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Total Receitas
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: theme.palette.success.main }}
              >
                {showValues ? formatCurrency(currency, totals.cashIn) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Total Despesas
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: theme.palette.error.main }}
              >
                {showValues ? formatCurrency(currency, totals.cashOut) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Saldo Periodo
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: totals.netBalance >= 0
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                }}
              >
                {showValues ? formatCurrency(currency, totals.netBalance) : '--'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 300 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        {!loading && chartData.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography color="text.secondary">
              Nenhum dado disponivel para o periodo selecionado
            </Typography>
          </Box>
        )}
        {!loading && chartData.length > 0 && (
          <BarChart
            xAxis={[
              {
                dataKey: 'period',
                scaleType: 'band',
                tickLabelStyle: {
                  fontSize: 11,
                },
              },
            ]}
            yAxis={[
              {
                ...yAxisConfig,
                tickNumber: 5,
              },
            ]}
            series={[
              {
                dataKey: 'cashIn',
                label: 'Receitas',
                color: '#22c55e',
                valueFormatter: (value) =>
                  showValues ? formatCurrency(currency, value || 0) : '--',
              },
              {
                dataKey: 'cashOut',
                label: 'Despesas',
                color: '#ef4444',
                valueFormatter: (value) =>
                  showValues ? formatCurrency(currency, value || 0) : '--',
              },
            ]}
            dataset={chartData}
            height={320}
            grid={{ horizontal: true }}
            margin={{ left: 70, right: 20, top: 40, bottom: 25 }}
            slotProps={{
              legend: {
                hidden: false,
                direction: 'row',
                position: { vertical: 'top', horizontal: 'right' },
                padding: 0,
                itemMarkWidth: 12,
                itemMarkHeight: 12,
                markGap: 5,
                itemGap: 15,
                labelStyle: {
                  fontSize: 12,
                },
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default CashFlowChart;
